import { autoinject, bindable, Container } from 'aurelia-framework';
import { DialogService } from 'aurelia-dialog';
import { I18N } from 'aurelia-i18n';
import { AsyncErrorHandler } from 'lib/ui';
import { PubSub } from 'lib/event/PubSub';
import { MooringExtraArticleItemDto, MooringOfferBomDto, UnitOfMeasure } from 'models/mooring';
import { ToastService } from 'services';
import { MooringOfferService } from 'services/mooring';
import { ConfirmDialog } from 'components/dialogs/confirm/confirm-dialog';
import { ExtraArticleDialog, ExtraArticleDialogArgs } from './extra-article-dialog';
import { OfferViewContext } from '../../OfferViewContext';

@autoinject
export class BomTable {
  @bindable mooringId!: number;
  @bindable offerId!: number;

  protected bom!: MooringOfferBomDto;
  protected groupByCategory = false;
  protected expanded: Record<number, boolean> = {};
  protected context = OfferViewContext.useContext(this.container);

  constructor(
    private container: Container,
    private mooringOfferService: MooringOfferService,
    private dialogService: DialogService,
    private toaster: ToastService,
    private t: I18N,
    private confirmation: ConfirmDialog,
    private pubsub: PubSub
  ) {
    this.pubsub.sub('list-entity:updated', () => void this.loadBom());
    this.pubsub.sub('entity:updated', () => void this.loadBom());
  }

  protected bind() {
    if (!this.mooringId) throw new Error('mooring-id.bind is required');
    if (!this.offerId) throw new Error('offer-id.bind is required');
  }

  @AsyncErrorHandler
  async attached() {
    await this.loadBom();
  }

  @AsyncErrorHandler
  private async loadBom() {
    const { data } = await this.mooringOfferService.getBom(this.offerId, this.groupByCategory);
    this.bom = data;
    this.expanded = {};
  }

  @AsyncErrorHandler
  protected async toggleGroupByCategory() {
    this.groupByCategory = !this.groupByCategory;
    await this.loadBom();
  }

  protected toggleExpand(productId: number) {
    this.expanded = {
      ...this.expanded,
      [productId]: this.expanded[productId] !== true,
    };
  }

  protected getUnitOfMeasure(unit: UnitOfMeasure) {
    if (unit == UnitOfMeasure.meter) return this.t.tr('general.meter_short');
    if (unit == UnitOfMeasure.pieces) return this.t.tr('general.qty');
    return '';
  }

  @AsyncErrorHandler
  protected async openAddExtraArticleDialog() {
    await this.dialogService
      .open({
        viewModel: ExtraArticleDialog,
        position: () => {
          //
        },
        model: { mooringId: this.mooringId } satisfies ExtraArticleDialogArgs,
      })
      .whenClosed(async (response) => {
        if (response.wasCancelled) return;
        await this.loadBom();
        this.toaster.showCreated();
      });
  }

  @AsyncErrorHandler
  protected async openEditExtraArticleDialog(extraArticle: MooringExtraArticleItemDto) {
    await this.dialogService
      .open({
        viewModel: ExtraArticleDialog,
        position: () => {
          //
        },
        model: { mooringId: this.mooringId, existingExtraArticle: extraArticle } satisfies ExtraArticleDialogArgs,
      })
      .whenClosed(async (response) => {
        if (response.wasCancelled) return;
        await this.loadBom();
        this.toaster.showUpdated();
      });
  }

  @AsyncErrorHandler
  protected async deleteExtraArticle(extraArticle: MooringExtraArticleItemDto) {
    const confirmed = await this.confirmation.confirmYesCancel('dialog.areYouSure', this.t.tr('general.deleteConfirmationText'));
    if (!confirmed) return;

    await this.mooringOfferService.deleteExtraArticle(extraArticle.Id);
    await this.loadBom();
    this.toaster.showDeleted();
  }

  protected unbind() {
    this.pubsub.unsub();
  }

  protected detached() {
    this.pubsub.unsub();
  }
}
